import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Lock, RefreshCw, LogOut, Inbox, Phone, Mail, CalendarDays, ArrowLeft, Loader2, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rosangela-admin-token";

export default function AdminInbox() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [messages, setMessages] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/contact`, { headers: { Authorization: `Bearer ${t}` } });
      if (res.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setMessages(null);
        return;
      }
      if (!res.ok) throw new Error("Não foi possível carregar as mensagens.");
      setMessages(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Erro ao entrar.");
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMessages(null);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-brand-navy bg-noise px-4 py-10 text-white sm:px-8">
      <div className="mx-auto max-w-4xl">
        <div className="mb-8 flex items-center justify-between">
          <a href="/" data-testid="admin-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-white/70 transition-colors hover:text-brand-orange">
            <ArrowLeft className="h-4 w-4" /> Voltar ao site
          </a>
          <span className="rounded-full bg-brand-orange px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest">Área restrita</span>
        </div>

        {!token ? (
          <motion.form
            onSubmit={login}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="admin-login-form"
            className="mx-auto mt-16 w-full max-w-md rounded-3xl border border-white/15 bg-white/10 p-8 shadow-2xl backdrop-blur-xl"
          >
            <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-orange text-white">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="text-center font-display text-2xl font-extrabold">Caixa de Mensagens</h1>
            <p className="mt-2 text-center text-sm text-white/70">
              Acesso restrito à equipe da campanha Rosangela da Saúde — 4030.
            </p>
            <label className="mt-7 block">
              <span className="mb-1.5 block text-sm font-bold text-white/80">Senha de acesso</span>
              <input
                type="password"
                required
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Digite a senha"
                className="w-full rounded-xl border border-white/20 bg-white/10 px-4 py-3 text-sm text-white outline-none placeholder:text-white/40 focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30"
              />
            </label>
            {error && (
              <p data-testid="admin-login-error" className="mt-3 rounded-xl bg-red-500/20 px-4 py-2.5 text-sm font-semibold text-red-200">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-brand-orange/30 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Entrar
            </button>
          </motion.form>
        ) : (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="flex items-center gap-3 font-display text-2xl sm:text-3xl font-extrabold">
                  <Inbox className="h-7 w-7 text-brand-orange" /> Caixa de Mensagens
                </h1>
                <p className="mt-1 text-sm text-white/70" data-testid="admin-message-count">
                  {messages ? `${messages.length} mensagem(ns) recebida(s) pelo formulário de contato` : "Carregando..."}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  data-testid="admin-refresh-button"
                  onClick={() => load(token)}
                  className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:border-brand-orange hover:text-brand-orange"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Atualizar
                </button>
                <button
                  type="button"
                  data-testid="admin-logout-button"
                  onClick={logout}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-red-500/80"
                >
                  <LogOut className="h-4 w-4" /> Sair
                </button>
              </div>
            </div>

            {error && (
              <p data-testid="admin-load-error" className="mb-4 rounded-xl bg-red-500/20 px-4 py-3 text-sm font-semibold text-red-200">{error}</p>
            )}

            {messages && messages.length === 0 && (
              <div data-testid="admin-empty-state" className="rounded-3xl border border-dashed border-white/25 bg-white/5 p-14 text-center">
                <Inbox className="mx-auto h-10 w-10 text-white/40" />
                <p className="mt-4 font-display text-lg font-bold text-white/80">Nenhuma mensagem por aqui ainda</p>
                <p className="mt-1 text-sm text-white/50">As mensagens enviadas pelo formulário de contato aparecerão nesta lista.</p>
              </div>
            )}

            <div data-testid="admin-message-list" className="space-y-4">
              {messages?.map((m) => (
                <motion.article
                  key={m.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`admin-message-${m.id}`}
                  className="rounded-3xl border border-white/15 bg-white/10 p-6 backdrop-blur-md"
                >
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h2 className="font-display text-lg font-bold">{m.nome}</h2>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-orange/20 px-3 py-1 text-xs font-bold text-brand-orange">
                      <Tag className="h-3 w-3" /> {m.assunto}
                    </span>
                  </div>
                  <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-white/85">{m.mensagem}</p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-4 text-xs text-white/60">
                    <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {m.telefone}</span>
                    <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {m.email}</span>
                    <span className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {new Date(m.created_at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                </motion.article>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
